import request from 'src/network';
import { AiAgent } from 'src/types/AiAgent';
import {
    AgentBehavior,
    AgentSkills,
    AiAgentV2,
    AiAgentV2Data,
    SkillClass,
    SkillResponse,
    VectorStoreConfiguration,
} from '../types/AiAgentV2';
import { Response } from '../types/Response';
import { SourcesDocumentV2 } from 'src/types/DocumentOrImageV2';
import { Agent, AiAgentV3, Flow } from 'src/types/AiAgentV3';

const apiPath = '/api/ai_agent/list';
const apiPath2 = '/api/ai_agent/agent';
const apiPath3 = '/api/ai_agent/upsert';
const apiPath4 = '/api/ai_agent/deploy';

export async function getAllAgentsService() {
    const response = await request.GET<AiAgent>(apiPath);
    response.items = response.items.map((t) => new AiAgent(t));
    return response;
}

export async function getAllAgentsServiceV2(): Promise<Response<AiAgentV2>> {
    const response = await request.GET<AiAgentV2Data>('/api/ai_agent_v2/agent/list');
    const items = response.items.map((t) => new AiAgentV2(t as AiAgentV2Data));

    return {
        ...(response as unknown as Response<AiAgentV2>),
        items,
    } as Response<AiAgentV2>;
}

export async function getAgentBehaviorListService(): Promise<Response<AgentBehavior>> {
    return await request.GET<AgentBehavior>('/api/ai_agent_v2/agent/behaviors/es');
}

export async function getAgentPoliciesListService(): Promise<Response<AgentBehavior>> {
    return await request.GET<AgentBehavior>('/api/ai_agent_v2/agent/policies/es');
}

export async function getAgentByIdService(id: string) {
    const response = await request.GET<AiAgent>(`${apiPath2}/${id}`);
    response.item = new AiAgent(response.item);
    return response;
}

export async function getAgentByIdServiceV2(id: string) {
    const response = await request.GET<AiAgentV2Data>(`/api/ai_agent_v2/agent/${id}`);
    return { ...response };
}

export async function getAllSkillsService() {
    const response = await request.GET<SkillResponse>(`/api/ai_agent_v2/agent/default_skills/es`);
    return { ...response };
}

export async function upsertAgentService(agent: any) {
    delete agent.created_at;
    delete agent.updated_at;

    const response = await request.POST<AiAgent>(`${apiPath3}`, agent);
    response.item = new AiAgent(response.item);
    return response;
}

export async function upsertAgentServiceV2(agent: AiAgentV2Data) {
    // delete agent.created_at;
    // delete agent.updated_at;

    // delete submittedData.skills;
    // delete submittedData.new_skills;
    // delete submittedData.created_at;
    // delete submittedData.updated_at;
    // delete submittedData.look_and_feel_voice;
    // delete submittedData.look_and_feel_whatsapp;

    const ignore = [
        'skills',
        'new_skills',
        'created_at',
        'updated_at',
        'look_and_feel_voice',
        'look_and_feel_whatsapp',
    ];

    const newAgent = Object.keys(agent).reduce((acc: { [key: string]: any }, key) => {
        if (!ignore.includes(key)) {
            acc[key] = agent[key as keyof AiAgentV2Data];
        }
        return acc;
    }, {});

    return await request.POST<AiAgent>(`/api/ai_agent_v2/agent`, newAgent);
}

export async function deleteAgentServiceV2(agent_id: number) {
    return await request.DELETE<AiAgent>(`/api/ai_agent_v2/agent/${agent_id}`);
}

export async function deleteAgentSkillServiceV2(agent_id: string, skill_id: number) {
    return await request.DELETE<AiAgent>(`/api/ai_agent_v2/agent/${agent_id}/skill/${skill_id}`);
}

export async function upsertAgentSkillServiceV2(skill: AgentSkills, agent_id: number, assign_skill = true) {
    const nSkill = new SkillClass(Object.assign({}, { ...skill }));

    const response = await request.POST<AiAgent>(`/api/ai_agent_v2/agent/skill`, nSkill.getSkillToUpsert());
    if (response?.success && response.item?.id && assign_skill) {
        const skill_id = response.item.id;
        return await request.POST<{ skill_id: number; success: boolean }>(
            `/api/ai_agent_v2/agent/${agent_id}/skill/${skill_id}`,
            {}
        );
    }

    return response;
}

export function deployAgentService(agent_id: number, user_id: number) {
    return request.POST(`${apiPath4}`, { user_id, agent_id });
}

export function deployAgentServiceV2(params: {
    agent_id: string;
    user: string;
    meta_data: string;
    deployed_chat: boolean;
    deployed_voice: boolean;
    deployed_whatsapp: boolean;
}) {
    return request.POST(`/api/ai_agent_v2/agent/deploy_agent`, params);
}

export function learnAgentServiceV2(params: { skill_id: string; user: string; test?: boolean; agent_id: string }) {
    return request.POST(`/api/ai_agent_v2/agent/learn_retriever`, params);
}

export function learnAgentService(agent_id: number) {
    return request.POST(`/api/ai_agent/agent/${agent_id}/learn`, {});
}

export function talkWithAgentService(agent_id: number, question: string) {
    return request.POST(`/api/ai_agent/chat_bot`, { agent_id, question, test: true });
}

export function reviewRagService(data: {
    folder: string | null;
    config: VectorStoreConfiguration;
    url: string[] | null;
}) {
    return request.POST<SourcesDocumentV2>(`/api/ai_agent_v2/agent/test_vector_store/es`, data);
}

export function translatePromptService(text: string) {
    return request.POST<string>('/api/ai_agent_v2/agent/translate', { text });
}

export function getReports() {
    return request.POST<string>('/api/data/v1/row/model/find', { row_model: 'sbx_crm_report' });
}

export async function getAgentData(baseUrl: string, agentId: string): Promise<Agent> {
    const fetchOptions = {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    };
    const response = await fetch(`${baseUrl}/api/ai_agent_v2/agent/${agentId}`, fetchOptions);
    const data = (await response.json()).item;
    data.look_and_feel_chat.extra_config = JSON.parse(data.look_and_feel_chat.extra_config);
    return data;
}

export function getAllAgentsServiceV3() {
    return request.GET<AiAgentV3>('/api/v3/agent');
}

export function getAgentByIdServiceV3(agent_id: string) {
    return request.GET<AiAgentV3>(`/api/v3/agent/${agent_id}`);
}

export function upsertAgentServiceV3(agent: AiAgentV3) {
    if (agent.id) {
        return request.PUT<AiAgentV3>(`/api/v3/agent`, agent);
    }
    return request.POST<AiAgentV3>(`/api/v3/agent`, agent);
}

export async function deleteAgentServiceV3(agent_id: number) {
    return await request.DELETE<AiAgent>(`/api/v3/agent/${agent_id}`);
}

export function getFlowAgentListService() {
    return request.GET<Flow>('/api/v1/flow/agent/list');
}

export function getAgentFlowRedirect() {
    return request.GET('/api/v1/flow/agent/list');
}
